import { isDeploymentPath } from '@/config/deployment'
export const KNOWN_ROLES = [
  'iam_superadmin',
  'iam_org_admin',
  'iam_org_user',
  'iam_new_user',
  'ticketing_admin',
  'ticketing_agent',
  'lead_agent',
  'ticket_viewer',
  'client_individual',
  'client_org_user',
  'work_admin',
  'work_manager',
  'work_member',
  'work_viewer',
  'work_timesheet_approver',
  'asset_admin',
  'asset_manager',
  'asset_technician',
  'asset_viewer',
  'asset_auditor',
  'business_admin',
  'business_viewer',
  'sales_manager',
  'sales_user',
  'purchase_manager',
  'purchase_user',
  'finance_admin',
  'finance_user',
  'contract_admin',
  'contract_manager',
  'contract_reviewer',
  'contract_viewer',
  'workflow_admin',
  'workflow_designer',
  'workflow_operator',
  'workflow_viewer',
  'waste_admin',
  'waste_mcf_operator',
  'waste_official',
] as const

export type KnownRole = typeof KNOWN_ROLES[number]

const KNOWN_ROLE_SET = new Set<string>(KNOWN_ROLES)

export const ROLE_LABELS: Record<string, string> = {
  admin: 'Admin',
  iam_superadmin: 'Super Admin',
  iam_org_admin: 'Org Admin',
  iam_org_user: 'Org User',
  iam_new_user: 'New User',
  iam_ticket: 'IAM Ticket',
  ticketing_admin: 'Ticketing Admin',
  ticketing_agent: 'Ticketing Agent',
  lead_agent: 'Lead Agent',
  ticket_viewer: 'Ticket Viewer',
  client_individual: 'Client Individual',
  client_org_user: 'Client Org User',
  work_admin: 'Work Admin',
  work_manager: 'Work Manager',
  work_member: 'Work Member',
  work_viewer: 'Work Viewer',
  waste_admin: 'Waste Admin',
  waste_mcf_operator: 'MCF Operator',
  waste_official: 'Panchayat Official',
  work_timesheet_approver: 'Timesheet Approver',
  asset_admin: 'Asset Admin',
  asset_manager: 'Asset Manager',
  asset_technician: 'Asset Technician',
  asset_viewer: 'Asset Viewer',
  asset_auditor: 'Asset Auditor',
  business_admin: 'Business Admin',
  business_viewer: 'Business Viewer',
  sales_manager: 'Sales Manager',
  sales_user: 'Sales User',
  purchase_manager: 'Purchase Manager',
  purchase_user: 'Purchase User',
  finance_admin: 'Finance Admin',
  finance_user: 'Finance User',
  contract_admin: 'Contract Admin',
  contract_manager: 'Contract Manager',
  contract_reviewer: 'Contract Reviewer',
  contract_viewer: 'Contract Viewer',
  workflow_admin: 'Workflow Admin',
  workflow_designer: 'Workflow Designer',
  workflow_operator: 'Workflow Operator',
  workflow_viewer: 'Workflow Viewer',
}

export const ROLE_DESCRIPTIONS: Record<string, string> = {
  iam_superadmin: 'Platform-level administrator with access across organizations and services.',
  iam_org_admin: 'Manages organization settings, users, access, and administrative configuration.',
  iam_org_user: 'Standard organization access; included for every created user.',
  iam_new_user: 'System-managed onboarding role that is not intended for manual assignment.',
  iam_ticket: 'Internal IAM and support access marker; not intended as a primary user role.',
  ticketing_admin: 'Manages tickets, ticket settings, assignments, analytics, live chat, and knowledge-base content.',
  lead_agent: 'Supervises ticket operations and can manage, review, publish, and delete knowledge-base content.',
  ticketing_agent: 'Works assigned tickets and can create and update knowledge-base articles.',
  ticket_viewer: 'Views tickets and related ticket information without ticket-management permissions.',
  client_individual: 'Lets an individual client submit and view their own support requests.',
  client_org_user: 'Lets a client-organization user access support requests for their organization.',
  work_admin: 'Administers task work, assignments, planning, timesheets, and approvals.',
  work_manager: 'Creates, assigns, manages, and deletes tasks; can access planning and approve time.',
  work_member: 'Creates and updates assigned work and records time.',
  work_viewer: 'Views tasks, notes, and timesheet information without editing work.',
  waste_admin: 'Manages MCF facilities, WhatsApp facilitators and all waste collections.',
  waste_mcf_operator: 'Receives, weighs and verifies collections at a Material Collection Facility.',
  waste_official: 'Oversees collections, approves high-volume or mismatched weights and handles exceptions.',
  work_timesheet_approver: 'Reviews and approves timesheets and accesses planning and reporting views.',
  asset_admin: 'Has full asset-management access, including deletion, imports, configuration, approvals, audits, and integrations.',
  asset_manager: 'Manages assets, inventory setup, allocations, workflows, and approvals without deletion permission.',
  asset_technician: 'Updates assets and manages asset allocations for operational support.',
  asset_viewer: 'Has read-only access to asset information.',
  asset_auditor: 'Views assets plus audit, credential, cloud-resource, SaaS, and compliance-related information.',
  business_admin: 'Has broad access across sales, purchasing, finance, invoices, and business reporting; can approve invoices.',
  business_viewer: 'Has read-only access to business, sales, purchasing, finance, and reporting areas.',
  sales_manager: 'Manages quotations, proformas, and sales orders.',
  sales_user: 'Works with quotations, proformas, and sales orders.',
  purchase_manager: 'Manages purchase orders.',
  purchase_user: 'Works with purchase orders.',
  finance_admin: 'Manages invoices, invoice settings, approvals, and financial reporting.',
  finance_user: 'Works with invoices and financial reporting without settings or approval permissions.',
  contract_admin: 'Has full contract-management access, including approvals.',
  contract_manager: 'Manages contracts without approval permission.',
  contract_reviewer: 'Reviews contracts and can approve them.',
  contract_viewer: 'Has read-only contract access.',
  workflow_admin: 'Has full access to process workflows, including publishing.',
  workflow_designer: 'Creates and publishes process workflows.',
  workflow_operator: 'Works with process workflows without publishing them.',
  workflow_viewer: 'Has read-only workflow access.',
}

// Groups KNOWN_ROLES by the backend service that owns them, so role pickers
// can render labeled sections instead of one flat list — with 35+ roles
// across services, which one belongs to which area stops being obvious.
export const ROLE_GROUPS: Array<{ label: string; roles: KnownRole[] }> = [
  { label: 'Organization (IAM)', roles: ['iam_superadmin', 'iam_org_admin', 'iam_org_user', 'iam_new_user'] },
  { label: 'Ticketing', roles: ['ticketing_admin', 'lead_agent', 'ticketing_agent', 'ticket_viewer'] },
  { label: 'Client portal', roles: ['client_individual', 'client_org_user'] },
  { label: 'Tasks & timesheets', roles: ['work_admin', 'work_manager', 'work_member', 'work_viewer', 'work_timesheet_approver'] },
  { label: 'Asset management', roles: ['asset_admin', 'asset_manager', 'asset_technician', 'asset_viewer', 'asset_auditor'] },
  { label: 'Business & sales', roles: ['business_admin', 'business_viewer', 'sales_manager', 'sales_user'] },
  { label: 'Purchasing', roles: ['purchase_manager', 'purchase_user'] },
  { label: 'Finance', roles: ['finance_admin', 'finance_user'] },
  { label: 'Contracts', roles: ['contract_admin', 'contract_manager', 'contract_reviewer', 'contract_viewer'] },
  { label: 'Workflow', roles: ['workflow_admin', 'workflow_designer', 'workflow_operator', 'workflow_viewer'] },
]

// Which subscribed-service module key(s) gate each ROLE_GROUPS section — a
// group is only shown in the Create User role picker if the org is
// subscribed to at least one of its listed services. A group with no entry
// here (e.g. 'Organization (IAM)') is always shown, same "no rule ⇒ always
// allowed" convention as ROUTE_PERMISSIONS. 'Ticketing' maps to both
// services since its roles also gate the client-mgmt /clients page — there's
// no dedicated client-mgmt role group.
export const ROLE_GROUP_SERVICES: Record<string, string[]> = {
  'Ticketing': ['ticket-mgmt', 'client-mgmt'],
  'Client portal': ['ticket-mgmt'],
  'Tasks & timesheets': ['hr-mgmt'],
  'Asset management': ['asset-mgmt'],
  'Business & sales': ['invoice-mgmt'],
  'Purchasing': ['invoice-mgmt'],
  'Finance': ['invoice-mgmt'],
  'Contracts': ['asset-mgmt'],
  'Workflow': ['asset-mgmt'],
}

// Roles for which a client association is meaningful in user provisioning.
// Ticketing staff roles such as Ticketing Admin or Agent are organization
// users, not client users, so they must not trigger the client selector.
export const CLIENT_RELEVANT_ROLES: KnownRole[] = [
  'client_individual', 'client_org_user',
]

const ADMIN_ROLES = ['iam_superadmin', 'iam_org_admin']
const SUPERADMIN_ROLES = ['iam_superadmin']
const ALL_AUTHENTICATED_ROLES = ['*']
const WORK_ROLES = ['work_admin', 'work_manager', 'work_member', 'work_viewer']
const WORK_MANAGER_ROLES = ['work_admin', 'work_manager']
const WORK_REPORT_ROLES = [...WORK_ROLES, 'work_timesheet_approver']
const TICKET_ADMIN_ROLES = ['ticketing_admin']
const TICKET_AGENT_ROLES = ['ticketing_admin', 'lead_agent', 'ticketing_agent']
const TICKET_READ_ROLES = ['ticketing_admin', 'lead_agent', 'ticketing_agent', 'ticket_viewer']
const TICKET_WITH_CLIENT_ROLES = [...TICKET_READ_ROLES, 'client_individual', 'client_org_user']
const KB_ROLES = ['ticketing_admin', 'ticketing_agent', 'lead_agent']
const KB_PUBLISH_ROLES = ['ticketing_admin', 'lead_agent']
const KB_ADMIN_ROLES = ['lead_agent']
const ASSET_ADMIN_ROLES = ['asset_admin']
const ASSET_ROLES = ['asset_admin', 'asset_manager', 'asset_technician', 'asset_viewer', 'asset_auditor']
const BUSINESS_SALES_ROLES = ['business_admin', 'sales_manager', 'sales_user', 'business_viewer']
const BUSINESS_PURCHASE_ROLES = ['business_admin', 'purchase_manager', 'purchase_user', 'business_viewer']
const FINANCE_ROLES = ['business_admin', 'finance_admin', 'finance_user', 'business_viewer']
const CONTRACT_ROLES = ['asset_admin', 'asset_manager', 'asset_viewer', 'asset_auditor', 'contract_admin', 'contract_manager', 'contract_reviewer', 'contract_viewer']
const WORKFLOW_ROLES = ['workflow_admin', 'workflow_designer', 'workflow_operator', 'workflow_viewer']
const WASTE_ADMIN_ROLES = [...ADMIN_ROLES, 'waste_admin']
const WASTE_ROLES = [...WASTE_ADMIN_ROLES, 'waste_mcf_operator', 'waste_official']

export const ROUTE_PERMISSIONS: Array<{ pattern: string; roles: string[] }> = [
  { pattern: '/', roles: ALL_AUTHENTICATED_ROLES },
  { pattern: '/profile', roles: ALL_AUTHENTICATED_ROLES },
  { pattern: '/settings', roles: ALL_AUTHENTICATED_ROLES },
  { pattern: '/help', roles: ALL_AUTHENTICATED_ROLES },
  { pattern: '/whats-new', roles: ALL_AUTHENTICATED_ROLES },

  { pattern: '/admin/datasources', roles: SUPERADMIN_ROLES },
  { pattern: '/admin/organization', roles: [...ADMIN_ROLES, 'iam_org_user'] },
  { pattern: '/admin/audit', roles: ADMIN_ROLES },
  { pattern: '/admin/billing', roles: ADMIN_ROLES },
  { pattern: '/admin/auth', roles: ADMIN_ROLES },
  { pattern: '/admin/branding', roles: ADMIN_ROLES },
  { pattern: '/admin/ai-provider', roles: [...ADMIN_ROLES, 'ticketing_admin'] },

  { pattern: '/tickets/settings', roles: TICKET_READ_ROLES },
  { pattern: '/tickets/analytics', roles: TICKET_AGENT_ROLES },
  { pattern: '/tickets/live-chat', roles: TICKET_AGENT_ROLES },
  { pattern: '/tickets/scheduled', roles: TICKET_AGENT_ROLES },
  { pattern: '/tickets', roles: TICKET_WITH_CLIENT_ROLES },
  { pattern: '/clients', roles: TICKET_READ_ROLES },
  { pattern: '/kb', roles: KB_ROLES },

  { pattern: '/tasks/timesheets/approvals', roles: [...WORK_MANAGER_ROLES, 'work_timesheet_approver'] },
  { pattern: '/tasks/planning', roles: WORK_REPORT_ROLES },
  { pattern: '/tasks/timesheets', roles: WORK_ROLES },
  { pattern: '/tasks', roles: WORK_ROLES },
  { pattern: '/notes', roles: WORK_ROLES },
  { pattern: '/reports', roles: [...WORK_REPORT_ROLES, 'business_admin', 'finance_admin', 'finance_user', 'business_viewer'] },

  { pattern: '/assets/contracts', roles: CONTRACT_ROLES },
  { pattern: '/assets/analytics', roles: ASSET_ADMIN_ROLES },
  { pattern: '/assets/agents', roles: ASSET_ADMIN_ROLES },
  { pattern: '/assets/notifications', roles: ASSET_ADMIN_ROLES },
  { pattern: '/assets/categories', roles: ['asset_admin', 'asset_manager'] },
  { pattern: '/assets/locations', roles: ['asset_admin', 'asset_manager'] },
  { pattern: '/assets/manufacturers', roles: ['asset_admin', 'asset_manager'] },
  { pattern: '/assets/vendors', roles: ['asset_admin', 'asset_manager'] },
  { pattern: '/assets/components', roles: ['asset_admin', 'asset_manager'] },
  { pattern: '/assets/allocations', roles: ['asset_admin', 'asset_manager', 'asset_technician'] },
  { pattern: '/assets/workflows', roles: ['asset_admin', 'asset_manager'] },
  { pattern: '/assets/saas-applications', roles: ['asset_admin', 'asset_manager', 'asset_auditor'] },
  { pattern: '/assets/cloud-resources', roles: ['asset_admin', 'asset_manager', 'asset_auditor'] },
  { pattern: '/assets/credentials', roles: ['asset_admin', 'asset_manager', 'asset_auditor'] },
  { pattern: '/assets/integrations', roles: ['asset_admin', 'asset_manager'] },
  { pattern: '/assets/audits', roles: ['asset_admin', 'asset_manager', 'asset_auditor'] },
  { pattern: '/assets/reorder-policies', roles: ['asset_admin', 'asset_manager'] },
  { pattern: '/assets', roles: ASSET_ROLES },

  { pattern: '/waste/simulator', roles: WASTE_ADMIN_ROLES },
  { pattern: '/waste/settings', roles: WASTE_ADMIN_ROLES },
  { pattern: '/waste', roles: WASTE_ROLES },

  { pattern: '/process-workflows', roles: WORKFLOW_ROLES },
  { pattern: '/quotations', roles: BUSINESS_SALES_ROLES },
  { pattern: '/proforma-invoices', roles: BUSINESS_SALES_ROLES },
  { pattern: '/sales-orders', roles: BUSINESS_SALES_ROLES },
  { pattern: '/purchase-orders', roles: BUSINESS_PURCHASE_ROLES },
  { pattern: '/invoices/settings', roles: ['business_admin', 'finance_admin'] },
  { pattern: '/invoices', roles: FINANCE_ROLES },

  { pattern: '/dashboards', roles: ADMIN_ROLES },
  { pattern: '/metrics', roles: ADMIN_ROLES },
  { pattern: '/logs', roles: ADMIN_ROLES },
  { pattern: '/traces', roles: ADMIN_ROLES },
  { pattern: '/query', roles: ADMIN_ROLES },
  { pattern: '/analytics', roles: ADMIN_ROLES },
  { pattern: '/correlations', roles: ADMIN_ROLES },
  { pattern: '/ai', roles: ADMIN_ROLES },
  { pattern: '/alerts', roles: ADMIN_ROLES },
  { pattern: '/incidents', roles: ADMIN_ROLES },
  { pattern: '/slos', roles: ADMIN_ROLES },
  { pattern: '/runbooks', roles: ADMIN_ROLES },
  { pattern: '/teams', roles: ADMIN_ROLES },
  { pattern: '/automation', roles: ADMIN_ROLES },
  { pattern: '/plugins', roles: ADMIN_ROLES },
]

export const ACTION_PERMISSIONS: Record<string, string[]> = {
  'task:create': ['work_admin', 'work_manager', 'work_member'],
  'task:update': ['work_admin', 'work_manager', 'work_member'],
  'task:assign': WORK_MANAGER_ROLES,
  'task:delete': WORK_MANAGER_ROLES,
  'time:approve': [...WORK_MANAGER_ROLES, 'work_timesheet_approver'],

  'ticket:create': [...TICKET_AGENT_ROLES, 'client_individual', 'client_org_user'],
  'ticket:update': TICKET_AGENT_ROLES,
  'ticket:assign': ['ticketing_admin', 'lead_agent'],
  'ticket:settings': TICKET_ADMIN_ROLES,

  'kb:view': KB_ROLES,
  'kb:search': KB_ROLES,
  'kb:create': KB_ROLES,
  'kb:update': KB_ROLES,
  'kb:category:create': KB_ROLES,
  'kb:attachment:upload': KB_ROLES,
  'kb:attachment:download': KB_ROLES,
  'kb:publish': KB_PUBLISH_ROLES,
  'kb:review': KB_ADMIN_ROLES,
  'kb:delete': KB_ADMIN_ROLES,
  'kb:manage_access': KB_PUBLISH_ROLES,

  'asset:create': ['asset_admin', 'asset_manager'],
  'asset:import': ['asset_admin', 'asset_manager'],
  'asset:update': ['asset_admin', 'asset_manager', 'asset_technician'],
  'asset:delete': ['asset_admin'],
  'asset:approve': ['asset_admin', 'asset_manager'],

  'invoice:approve': ['business_admin', 'finance_admin'],
  'contract:approve': ['asset_admin', 'contract_admin', 'contract_reviewer'],
  'workflow:publish': ['workflow_admin', 'workflow_designer'],
  'organization:create': ['iam_superadmin'],
  'organization:delete': ['iam_superadmin'],
  'user:create': ADMIN_ROLES,
  // Ticketing admins may manage client portal users only — IAM enforces the
  // same limit (see portal_user_authz.go in the backend).
  'client_portal_user:manage': [...ADMIN_ROLES, 'ticketing_admin'],
  'user:update': ADMIN_ROLES,
  'user:delete': ADMIN_ROLES,
}

export function normalizeRoleName(role: string) {
  const normalized = role.trim().toLowerCase().replace(/[\s-]+/g, '_')
  if (normalized === 'admin' || normalized === 'superadmin' || normalized === 'super_admin' || normalized === 'iam_admin') return 'iam_superadmin'
  if (normalized === 'org_admin' || normalized === 'organization_admin' || normalized === 'organisation_admin') return 'iam_org_admin'
  if (normalized === 'org_user' || normalized === 'organization_user' || normalized === 'organisation_user') return 'iam_org_user'
  if (normalized === 'ticketing_lead_agent') return 'lead_agent'
  if (normalized === 'ticketing_viewer') return 'ticket_viewer'
  if (normalized === 'iam_new_user') return 'iam_new_user'
  return normalized
}

export function normalizeRoles(roles: readonly string[] | undefined | null) {
  const normalized = (roles ?? [])
    .filter(Boolean)
    .map(normalizeRoleName)
    .filter(role => KNOWN_ROLE_SET.has(role))
  return Array.from(new Set(normalized))
}

export function roleLabel(role: string) {
  const normalized = normalizeRoleName(role)
  return ROLE_LABELS[normalized] ?? normalized.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())
}

export function roleDescription(role: string) {
  return ROLE_DESCRIPTIONS[normalizeRoleName(role)] ?? 'Access level for this role.'
}

const HIDDEN_DISPLAY_ROLES = new Set(['iam_ticket'])

const ROLE_DISPLAY_HIERARCHIES: string[][] = [
  ['iam_superadmin', 'iam_org_admin', 'iam_org_user'],
  ['ticketing_admin', 'lead_agent', 'ticketing_agent', 'ticket_viewer'],
  ['client_individual', 'client_org_user'],
  ['asset_admin', 'asset_manager', 'asset_technician', 'asset_viewer', 'asset_auditor'],
]

const ROLE_DISPLAY_ORDER = [
  'iam_superadmin',
  'iam_org_admin',
  'iam_org_user',
  'ticketing_admin',
  'lead_agent',
  'ticketing_agent',
  'ticket_viewer',
  'client_individual',
  'client_org_user',
  'work_admin',
  'work_manager',
  'work_member',
  'work_viewer',
  'work_timesheet_approver',
  'asset_admin',
  'asset_manager',
  'asset_technician',
  'asset_viewer',
  'asset_auditor',
  'business_admin',
  'business_viewer',
  'sales_manager',
  'sales_user',
  'purchase_manager',
  'purchase_user',
  'finance_admin',
  'finance_user',
  'contract_admin',
  'contract_manager',
  'contract_reviewer',
  'contract_viewer',
  'workflow_admin',
  'workflow_designer',
  'workflow_operator',
  'workflow_viewer',
]

const ROLE_DISPLAY_RANK = new Map(ROLE_DISPLAY_ORDER.map((role, index) => [role, index]))

export function displayRoles(roles: readonly string[] | undefined | null) {
  const normalized = normalizeRoles(roles).filter(role => !HIDDEN_DISPLAY_ROLES.has(role))
  const visible = new Set(normalized)
  if (visible.size > 1) visible.delete('iam_org_user')

  for (const hierarchy of ROLE_DISPLAY_HIERARCHIES) {
    const highestRole = hierarchy.find(role => visible.has(role))
    if (!highestRole) continue
    for (const lowerRole of hierarchy.slice(hierarchy.indexOf(highestRole) + 1)) {
      visible.delete(lowerRole)
    }
  }

  return Array.from(visible).sort((a, b) => {
    const aRank = ROLE_DISPLAY_RANK.get(a) ?? Number.MAX_SAFE_INTEGER
    const bRank = ROLE_DISPLAY_RANK.get(b) ?? Number.MAX_SAFE_INTEGER
    if (aRank !== bRank) return aRank - bRank
    return a.localeCompare(b)
  })
}

function routePatternScore(pattern: string) {
  return pattern === '/' ? 1 : pattern.split('/').length * 100 + pattern.length
}

function matchesPattern(path: string, pattern: string) {
  if (pattern === '/') return path === '/'
  return path === pattern || path.startsWith(`${pattern}/`)
}

export function rolesAllow(userRoles: readonly string[] | undefined | null, allowedRoles: readonly string[], legacyRole?: string) {
  if (allowedRoles.includes('*')) return true
  const roles = normalizeRoles(userRoles)
  if (roles.includes('iam_superadmin')) return true
  if (roles.some(role => allowedRoles.includes(role))) return true
  if (legacyRole === 'admin') return allowedRoles.some(role => ADMIN_ROLES.includes(role))
  return false
}

export function routeRuleForPath(path: string) {
  const normalizedPath = path.split('?')[0] || '/'
  return ROUTE_PERMISSIONS
    .filter(rule => matchesPattern(normalizedPath, rule.pattern))
    .sort((a, b) => routePatternScore(b.pattern) - routePatternScore(a.pattern))[0]
}

export function canAccessPath(path: string, userRoles: readonly string[] | undefined | null, legacyRole?: string) {
  if (!isDeploymentPath(path) && !normalizeRoles(userRoles).includes('iam_superadmin')) return false
  const rule = routeRuleForPath(path)
  if (!rule) return true
  return rolesAllow(userRoles, rule.roles, legacyRole)
}

export function canPerformAction(action: string, userRoles: readonly string[] | undefined | null, legacyRole?: string) {
  const allowedRoles = ACTION_PERMISSIONS[action]
  if (!allowedRoles) return true
  return rolesAllow(userRoles, allowedRoles, legacyRole)
}

// Path → owning module key, mirroring pages/Home.tsx's own MODULE_REGISTRY
// (the existing source of truth for this mapping). A path with no entry here
// is never subscription-gated (admin pages, profile, observability tooling,
// etc.) — same "no rule ⇒ always allowed" convention as ROUTE_PERMISSIONS.
export const MODULE_ROUTE_MAP: Array<{ pattern: string; service: string }> = [
  { pattern: '/tickets', service: 'ticket-mgmt' },
  { pattern: '/kb', service: 'ticket-mgmt' },
  { pattern: '/clients', service: 'client-mgmt' },
  { pattern: '/tasks', service: 'hr-mgmt' },
  { pattern: '/notes', service: 'hr-mgmt' },
  { pattern: '/reports', service: 'hr-mgmt' },
  { pattern: '/assets', service: 'asset-mgmt' },
  { pattern: '/process-workflows', service: 'asset-mgmt' },
  { pattern: '/invoices', service: 'invoice-mgmt' },
  { pattern: '/quotations', service: 'invoice-mgmt' },
  { pattern: '/proforma-invoices', service: 'invoice-mgmt' },
  { pattern: '/sales-orders', service: 'invoice-mgmt' },
  { pattern: '/purchase-orders', service: 'invoice-mgmt' },
]

export function moduleRuleForPath(path: string) {
  const normalizedPath = path.split('?')[0] || '/'
  return MODULE_ROUTE_MAP
    .filter(rule => matchesPattern(normalizedPath, rule.pattern))
    .sort((a, b) => routePatternScore(b.pattern) - routePatternScore(a.pattern))[0]
}

export function isSubscribedPath(path: string, userRoles: readonly string[] | undefined | null, subscribedServices: readonly string[] | undefined | null) {
  if (normalizeRoles(userRoles).includes('iam_superadmin')) return true
  const rule = moduleRuleForPath(path)
  if (!rule) return true
  // Subscription data is fetched after login and may be unavailable for
  // non-admin staff. Backend module auth remains the source of truth, so avoid
  // hiding valid routes just because the frontend could not load this hint.
  if (subscribedServices == null) return true
  return subscribedServices.includes(rule.service)
}
